import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import './Profile.css';

const SCREEN_LABELS = {
  dashboard: 'Dashboard',
  customers: 'Customers',
  employees: 'Employees',
  clause_library: 'Clause library',
  key_fields: 'Key fields',
  msa: 'MSA',
  sow: 'SOW',
  engagements: 'Engagements',
  invoices: 'Billing / invoices',
  timesheets: 'Timesheets',
  approvals: 'Approvals',
  reports: 'Reports',
  user_management: 'User management',
  settings: 'Settings',
};

function initials(fullName, username) {
  const s = (fullName || username || 'U').trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function formatMemberSince(createdAt) {
  if (!createdAt) return '—';
  const raw = String(createdAt).trim();
  const d = raw.includes('T') ? new Date(raw) : new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function Profile() {
  const { user, screenAccess, dashboards } = useAuth();
  const [accountDetail, setAccountDetail] = useState(null);
  const [emailCopied, setEmailCopied] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(setAccountDetail).catch(() => {});
  }, []);

  const u = accountDetail || user || {};
  const role = u.role;
  const isAdmin = role === 'Admin';
  const isHr = role === 'HR Manager';

  const accessRows = useMemo(
    () =>
      [...(screenAccess || [])].sort((a, b) =>
        (SCREEN_LABELS[a.screen] || a.screen).localeCompare(SCREEN_LABELS[b.screen] || b.screen)
      ),
    [screenAccess]
  );

  const viewableLabels = useMemo(
    () =>
      accessRows
        .filter((r) => r.can_view)
        .map((r) => SCREEN_LABELS[r.screen] || r.screen),
    [accessRows]
  );

  const dashList = dashboards || [];

  const copyWorkEmail = () => {
    const em = String(u.email || '').trim();
    if (!em) return;
    const done = () => {
      setEmailCopied(true);
      window.setTimeout(() => setEmailCopied(false), 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(em).then(done).catch(() => {});
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = em;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>My profile</h1>
      </div>
      <div className="page-content profile-page">
        <nav className="page-back-nav" aria-label="Breadcrumb">
          <Link to="/" className="page-back-link">
            ← Back to dashboard
          </Link>
        </nav>

        <section className="profile-card-main" aria-labelledby="profile-name-heading">
          <div className="profile-card-main-top">
            <div className="profile-avatar-simple" aria-hidden="true">
              {initials(u.full_name, u.username)}
            </div>
            <div className="profile-card-main-intro">
              <h2 id="profile-name-heading" className="profile-name-simple">
                {u.full_name || u.username || 'User'}
              </h2>
              <p className="profile-role-row">
                <span className="profile-role-text">{u.role || 'User'}</span>
                {u.is_active === 0 ? (
                  <span className="profile-status-badge profile-status-badge--off">Inactive</span>
                ) : (
                  <span className="profile-status-badge profile-status-badge--on">Active</span>
                )}
              </p>
            </div>
          </div>

          <dl className="profile-dl">
            <div className="profile-dl-row">
              <dt>Username</dt>
              <dd>{u.username || '—'}</dd>
            </div>
            <div className="profile-dl-row">
              <dt>Work email</dt>
              <dd className="profile-dl-row-dd--email">
                <span>{u.email?.trim() ? u.email : '—'}</span>
                {u.email?.trim() ? (
                  <button type="button" className="profile-copy-btn" onClick={copyWorkEmail}>
                    {emailCopied ? 'Copied' : 'Copy'}
                  </button>
                ) : null}
              </dd>
            </div>
            <div className="profile-dl-row">
              <dt>Member since</dt>
              <dd>{formatMemberSince(u.created_at)}</dd>
            </div>
            <div className="profile-dl-row">
              <dt>User ID</dt>
              <dd className="tabular-nums">{u.id ?? '—'}</dd>
            </div>
          </dl>
        </section>

        <section className="profile-card-secondary" aria-labelledby="profile-access-heading">
          <h3 id="profile-access-heading" className="profile-section-title">
            Access
          </h3>
          {isAdmin ? (
            <p className="profile-section-text">
              You are signed in with administrator privileges across the workspace.
            </p>
          ) : null}
          {isHr && !isAdmin ? (
            <p className="profile-section-text">
              Your role includes employee records and related HR settings. Other menus follow your organisation’s
              assignments.
            </p>
          ) : null}
          {!isAdmin && viewableLabels.length > 0 ? (
            <>
              <p className="profile-section-label">Screens you can open</p>
              <ul className="profile-access-list">
                {viewableLabels.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </>
          ) : null}
          {!isAdmin && !isHr && viewableLabels.length === 0 ? (
            <p className="profile-section-text muted">
              No individual screen list is stored for this account. If a menu is missing, ask your administrator to
              review your access.
            </p>
          ) : null}

          {dashList.length > 0 ? (
            <div className="profile-dash-block">
              <p className="profile-section-label">Dashboards</p>
              <ul className="profile-dash-links">
                {dashList.map((d) => (
                  <li key={d.slug || d.id || d.name}>
                    {d.slug ? (
                      <Link to={`/dashboards/${d.slug}`}>{d.name || d.slug}</Link>
                    ) : (
                      <span>{d.name || d.slug || 'Dashboard'}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>

        <p className="profile-foot">
          Password and role changes are handled by an administrator. Use <strong>Sign out</strong> in the sidebar when
          you are finished on this device.
        </p>
      </div>
    </>
  );
}
